import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/api';
import { flushSync } from 'react-dom';
import Modal from '../../components/common/Modal';
import { useLanguage } from '../../context/LanguageContext';
import { translateDynamic, getEntityTranslation } from '../../context/LanguageContext';
import VisualTableSelector, { buildTableGroups, resolveOccupation } from '../../components/pos/VisualTableSelector';
import WaiterOrderDrawer from '../../components/pos/WaiterOrderDrawer';
import useBusinessDayGuard from '../../hooks/pos/useBusinessDayGuard';
import api from '../../services/api';
import tenantStorage from '../../services/tenantStorage';
import { C } from '../../styles/theme';
import dedupeByName from '../../utils/dedupeByName';
import {
    reservationService,
    waitingListService,
    guestService,
    occupationService,
    diningOptionService,
} from '../../services/api';

const FONT = "'Plus Jakarta Sans', sans-serif";

/* ── SVG Icons ────────────────────────────────────── */
const BackArrow = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
        stroke={C.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
);

const UsersIcon = ({ size = 14, color = C.muted }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

const ClockSmallIcon = ({ size = 12, color = C.muted }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
);

const PlusIcon = ({ size = 16, color = C.white }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

const CloseIcon = ({ size = 18, color = C.muted }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

/* ── Helpers ──────────────────────────────────────── */
const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem > 0 ? `${hrs}h${rem}m` : `${hrs}h`;
};

const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const statusBadge = (status, t) => {
    const map = {
        waiting: { bg: C.amberLight, color: C.amber, label: t('waiting') || 'Waiting' },
        notified: { bg: '#eff6ff', color: '#2563eb', label: t('notified') || 'Notified' },
        seated: { bg: C.greenLight, color: C.green, label: t('seated') || 'Seated' },
        booked: { bg: '#eff6ff', color: '#2563eb', label: t('booked') || 'Booked' },
        confirmed: { bg: C.greenLight, color: C.green, label: t('confirmed') || 'Confirmed' },
        cancelled: { bg: C.redLight, color: C.red, label: t('cancelled') || 'Cancelled' },
        no_show: { bg: C.redLight, color: C.red, label: t('no_show') || 'No Show' },
        completed: { bg: '#f1f5f9', color: C.muted, label: t('completed') || 'Completed' },
    };
    const s = map[status] || { bg: '#f1f5f9', color: C.muted, label: status || '' };
    return (
        <span style={{
            display: 'inline-block', fontSize: 10, fontWeight: 700,
            padding: '2px 7px', borderRadius: 6,
            background: s.bg, color: s.color,
            textTransform: 'capitalize', whiteSpace: 'nowrap',
        }}>
            {s.label}
        </span>
    );
};

/* ================================================================== */
/*  TableServicePage                                                   */
/* ================================================================== */
const TableServicePage = () => {
    const navigate = useNavigate();
    const { tenantConfig, username, setAuthenticatedState, getCurrency } = useAuth();
    const currency = getCurrency();
    const { t, language, translationDetails, changeLanguage } = useLanguage();

    // Block direct-URL access when there is no active business day
    useBusinessDayGuard();

    /* ── State ─────────────────────────────────────── */
    const [selectedDiningOption, setSelectedDiningOption] = useState(null);
    const [diningOptions, setDiningOptions] = useState([]);
    const [diningOptionObjects, setDiningOptionObjects] = useState({});
    const [activeOccupations, setActiveOccupations] = useState([]);
    const [waitlistEntries, setWaitlistEntries] = useState([]);
    const [todayReservations, setTodayReservations] = useState([]);
    const [activeTab, setActiveTab] = useState('waitlist');
    const [tablePopover, setTablePopover] = useState(null);
    const [orderDrawer, setOrderDrawer] = useState(null);
    const [walkinPartySize, setWalkinPartySize] = useState(2);
    const [seatMode, setSeatMode] = useState(null);
    const [mergeMode, setMergeMode] = useState(false);
    const [mergeSelection, setMergeSelection] = useState([]);  // table numbers; first = anchor
    const [showAddWaitlistModal, setShowAddWaitlistModal] = useState(false);
    const [pendingOrders, setPendingOrders] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Cashier PIN modal
    const [showPinModal, setShowPinModal] = useState(false);
    const [pinValue, setPinValue] = useState('');
    const [pinLoading, setPinLoading] = useState(false);

    const handlePinSubmit = async (e) => {
        e.preventDefault();
        if (!pinValue || pinValue.length !== 6) { setError(t('Please enter your 6-digit PIN')); return; }
        try {
            setPinLoading(true);
            const tenantId = localStorage.getItem('tenant_id');
            const emp = await authService.employeeLogin(tenantId, pinValue);
            if (!emp.is_cashier && emp.user_role !== 'admin' && emp.user_role !== 'manager') {
                setError(t('Access denied. Cashier access is required for this function.'));
                setPinLoading(false);
                return;
            }
            if (emp.preferred_language) changeLanguage(emp.preferred_language);
            flushSync(() => {
                setAuthenticatedState({
                    isAuthenticated: true, userRole: emp.user_role,
                    username: emp.employee_name, tenantId,
                    employeeId: emp.employee_id, authType: 'function',
                });
                setShowPinModal(false);
                setPinLoading(false);
            });
            navigate('/functions/cashier-sales');
        } catch {
            setError(t('Invalid employee credentials'));
            setPinLoading(false);
        }
    };

    // Add-to-waitlist form
    const [wlGuestName, setWlGuestName] = useState('');
    const [wlPhone, setWlPhone] = useState('');
    const [wlPartySize, setWlPartySize] = useState(2);
    const [wlSubmitting, setWlSubmitting] = useState(false);

    // Add-reservation form
    const [showAddReservationModal, setShowAddReservationModal] = useState(false);
    const [resGuestName, setResGuestName] = useState('');
    const [resPhone, setResPhone] = useState('');
    const [resPartySize, setResPartySize] = useState(2);
    const [resDate, setResDate] = useState('');
    const [resTime, setResTime] = useState('');
    const [resNotes, setResNotes] = useState('');
    const [resSubmitting, setResSubmitting] = useState(false);

    // For table popover positioning
    const floorRef = useRef(null);
    const lastClickPos = useRef({ x: 0, y: 0 });
    const tableClicked = useRef(false);

    /* ── Data loading ──────────────────────────────── */
    const loadDiningOptions = useCallback(async () => {
        try {
            const all = dedupeByName(await diningOptionService.getAll());
            const withTables = all.filter(d => {
                if (!d.tables_config || Object.keys(d.tables_config).length === 0) return false;
                const cfg = d.tables_config;
                if (Array.isArray(cfg)) return cfg.some(t => t.x !== undefined && t.y !== undefined);
                if (typeof cfg === 'object') return Object.values(cfg).some(v => {
                    const entry = Array.isArray(v) && v.length > 0 ? v[0] : v;
                    return entry && entry.x !== undefined && entry.y !== undefined;
                });
                return false;
            });
            const names = withTables.map(d => d.name);
            const objMap = {};
            withTables.forEach(d => { objMap[d.name] = d; });
            setDiningOptions(withTables);
            setDiningOptionObjects(objMap);
            if (withTables.length > 0 && !selectedDiningOption) {
                setSelectedDiningOption(withTables[0].name);
            }
        } catch (e) {
            console.error('Failed to load dining options:', e);
        }
    }, []);

    const loadPendingOrders = useCallback(async () => {
        try {
            // Scope to the active business day (salesDate), not the calendar date —
            // a business day can span midnight, and the combine relies on every
            // active table draft being present here.
            const salesDate = await tenantStorage.getState('salesDate');
            const params = salesDate ? { sales_date: salesDate } : {};
            const response = await api.get('/tenant-storage/orders', { params });
            const { orders } = response.data || {};
            setPendingOrders(orders || {});
        } catch (e) { console.error('loadPendingOrders failed:', e); setPendingOrders({}); }
    }, []);

    const loadOccupations = useCallback(async () => {
        try {
            const occs = await occupationService.listActive();
            setActiveOccupations(occs);
        } catch (e) {
            console.error('Failed to load occupations:', e);
        }
    }, []);

    const loadWaitlist = useCallback(async () => {
        try {
            const entries = await waitingListService.list({ status: 'waiting,notified' });
            setWaitlistEntries(Array.isArray(entries) ? entries : []);
        } catch (e) {
            console.error('Failed to load waitlist:', e);
        }
    }, []);

    const loadReservations = useCallback(async () => {
        try {
            const today = new Date();
            const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            const res = await reservationService.list({ date: dateStr });
            setTodayReservations(Array.isArray(res) ? res : []);
        } catch (e) {
            console.error('Failed to load reservations:', e);
        }
    }, []);

    const refreshAll = useCallback(async () => {
        await Promise.all([loadOccupations(), loadWaitlist(), loadReservations(), loadPendingOrders()]);
    }, [loadOccupations, loadWaitlist, loadReservations, loadPendingOrders]);

    // Initial load
    useEffect(() => {
        let mounted = true;
        const init = async () => {
            await loadDiningOptions();
            await refreshAll();
            if (mounted) setLoading(false);
        };
        init();
        return () => { mounted = false; };
    }, []);

    // Refresh every 30s
    useEffect(() => {
        const id = setInterval(refreshAll, 30000);
        return () => clearInterval(id);
    }, [refreshAll]);

    /* ── Derived ───────────────────────────────────── */
    const selectedDO = diningOptionObjects[selectedDiningOption];
    const tablesConfig = selectedDO?.tables_config || {};

    // Build diningOptions map for VisualTableSelector
    const diningOptionsMap = {};
    diningOptions.forEach(d => {
        if (d.tables_config) diningOptionsMap[d.name] = d.tables_config;
    });

    // Filter occupations for selected dining option
    const filteredOccupations = activeOccupations.filter(o => {
        if (!selectedDO) return false;
        return String(o.dining_option_id) === String(selectedDO.id);
    });

    // Merged-group lookup for the current dining option
    const { groupByAnchor } = buildTableGroups(filteredOccupations);

    // Today's reservations split
    const now = new Date();
    const upcomingReservations = todayReservations.filter(r =>
        ['booked', 'confirmed'].includes(r.status) && new Date(r.scheduled_for) >= now
    );
    const pastReservations = todayReservations.filter(r =>
        !(['booked', 'confirmed'].includes(r.status) && new Date(r.scheduled_for) >= now)
    );

    /* ── Actions ───────────────────────────────────── */
    const handleTableTap = (tableNumStr) => {
        // Merge mode: tapping toggles inclusion in the selection (first = anchor)
        if (mergeMode) {
            setMergeSelection(prev => {
                const s = prev.map(String);
                return s.includes(tableNumStr)
                    ? s.filter(x => x !== tableNumStr)
                    : [...s, tableNumStr];
            });
            return;
        }

        // If in seat mode, try to seat
        if (seatMode) {
            handleSeatOnTable(tableNumStr);
            return;
        }

        // Find occupation for this table; if it's a merged member, resolve to the anchor.
        const occ = resolveOccupation(filteredOccupations, tableNumStr);
        // When a member table is tapped, act on the anchor table.
        if (occ) tableNumStr = String(occ.table_number);
        const tableKey = `${selectedDiningOption}-${tableNumStr}`;
        const hasOrder = !!(pendingOrders[tableKey] || (occ && occ.first_order_at));

        // Use stored click position
        const containerWidth = floorRef.current?.offsetWidth || 600;
        const containerHeight = floorRef.current?.offsetHeight || 400;

        let status = 'available';
        if (occ) {
            if (occ.paid_at) status = 'paid';
            else if (occ.bill_requested_at) status = 'awaiting_bill';
            else if (hasOrder) status = 'ordered';
            else status = 'seated';
        } else if (pendingOrders[tableKey]) {
            status = 'ordered';
        }

        setWalkinPartySize(2);
        setTablePopover({
            tableNumber: tableNumStr,
            x: Math.max(10, Math.min(lastClickPos.current.x, containerWidth - 220)),
            y: Math.max(10, Math.min(lastClickPos.current.y, containerHeight - 200)),
            occupation: occ || null,
            status,
        });
    };

    const handleSeatOnTable = async (tableNumStr) => {
        if (!seatMode || !selectedDO) return;
        try {
            if (seatMode.type === 'walkin') {
                await occupationService.create({
                    dining_option_id: selectedDO.id,
                    table_number: tableNumStr,
                    party_size: seatMode.partySize || 2,
                    guest_name: seatMode.guestName || null,
                });
                setSuccess(t('walk_in_seated') || 'Walk-in seated successfully');
            } else if (seatMode.type === 'waitlist') {
                await waitingListService.seat(seatMode.id, {
                    dining_option_id: selectedDO.id,
                    table_number: tableNumStr,
                });
                setSuccess(t('guest_seated_from_waitlist') || 'Guest seated from waitlist');
            } else if (seatMode.type === 'reservation') {
                await reservationService.seat(seatMode.id, {
                    dining_option_id: selectedDO.id,
                    table_number: tableNumStr,
                });
                setSuccess(t('reservation_seated') || 'Reservation seated');
            } else if (seatMode.type === 'change_table') {
                await occupationService.changeTable(seatMode.occupationId, tableNumStr);
                // Move the order draft too — moving the occupation alone leaves the
                // bill stranded on the old table key (and out of any later merge).
                const oldKey = `${selectedDiningOption}-${seatMode.fromTable}`;
                const movingOrder = pendingOrders[oldKey];
                const movingOrderId = movingOrder && (movingOrder._orderId || movingOrder.order_id);
                if (movingOrderId) {
                    const newKey = `${selectedDiningOption}-${tableNumStr}`;
                    await tenantStorage.changeOrderTable(movingOrderId, newKey, selectedDiningOption, tableNumStr);
                }
                setSuccess(t('table_changed') || 'Table changed successfully');
            }
            setSeatMode(null);
            await refreshAll();
            setTimeout(() => setSuccess(''), 3000);
        } catch (e) {
            console.error('Seat failed:', e);
            setError(e.response?.data?.detail || t('failed_to_seat') || 'Failed to seat');
            setTimeout(() => setError(''), 4000);
        }
    };

    const handleSeatWalkIn = async () => {
        if (!selectedDO || !tablePopover) return;
        try {
            await occupationService.create({
                dining_option_id: selectedDO.id,
                table_number: tablePopover.tableNumber,
                party_size: walkinPartySize || 2,
            });
            setTablePopover(null);
            await refreshAll();
            setSuccess(t('walk_in_seated') || 'Walk-in seated successfully');
            setTimeout(() => setSuccess(''), 3000);
        } catch (e) {
            setError(e.response?.data?.detail || t('failed_to_seat') || 'Failed to seat');
            setTimeout(() => setError(''), 4000);
        }
    };

    const handleNewOrder = (occ) => {
        if (!selectedDiningOption || !occ) return;
        setTablePopover(null);
        setOrderDrawer({ mode: 'new', tableNumber: String(occ.table_number), occupation: occ, existingOrder: null });
    };

    const handleAddItems = (occ) => {
        if (!selectedDiningOption || !occ) return;
        const tableKey = `${selectedDiningOption}-${occ.table_number}`;
        setTablePopover(null);
        setOrderDrawer({
            mode: 'append', tableNumber: String(occ.table_number),
            occupation: occ, existingOrder: pendingOrders[tableKey] || null,
        });
    };

    const handleMarkServed = async (occ) => {
        try {
            await occupationService.markServed(occ.id);
            setTablePopover(null);
            await refreshAll();
            setSuccess(t('marked_served') || 'Marked as served');
            setTimeout(() => setSuccess(''), 3000);
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed');
            setTimeout(() => setError(''), 4000);
        }
    };

    const startChangeTable = (occ) => {
        setTablePopover(null);
        setSeatMode({
            type: 'change_table',
            occupationId: occ.id,
            fromTable: occ.table_number,
            partySize: occ.party_size,
            guestName: occ.guest_name || occ.guest?.name || '',
        });
        setSuccess(t('tap_new_table') || 'Tap the table to move to');
        setTimeout(() => setSuccess(''), 5000);
    };

    const handleRequestBill = async (occ) => {
        try {
            await occupationService.requestBill(occ.id);
            setTablePopover(null);
            await refreshAll();
            setSuccess(t('bill_requested') || 'Bill requested');
            setTimeout(() => setSuccess(''), 3000);
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed');
            setTimeout(() => setError(''), 4000);
        }
    };

    const handleClearTable = async (occ) => {
        try {
            await occupationService.clearTable(occ.id);
            setTablePopover(null);
            await refreshAll();
            setSuccess(t('table_cleared') || 'Table cleared');
            setTimeout(() => setSuccess(''), 3000);
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed');
            setTimeout(() => setError(''), 4000);
        }
    };

    /* ── Merge / split ─────────────────────────────── */
    const startMergeMode = () => {
        setTablePopover(null);
        setSeatMode(null);
        setMergeSelection([]);
        setMergeMode(true);
    };

    const cancelMergeMode = () => {
        setMergeMode(false);
        setMergeSelection([]);
    };

    // Fold each member table's draft order into the anchor's, then delete the
    // member drafts. Totals are additive (each table carried its own bill).
    const combineMemberOrdersIntoAnchor = async (anchorTable, memberTables) => {
        const salesDate = await tenantStorage.getState('salesDate');
        const anchorKey = `${selectedDiningOption}-${anchorTable}`;
        let base = pendingOrders[anchorKey] ? { ...pendingOrders[anchorKey] } : null;
        let anchorOrderId = base ? (base._orderId || base.order_id || null) : null;
        const num = (v) => parseFloat(v) || 0;

        // First fold every member order into `base` in memory (no deletes yet).
        const toDelete = [];
        for (const mt of memberTables) {
            const mKey = `${selectedDiningOption}-${mt}`;
            const mOrder = pendingOrders[mKey];
            if (!mOrder) continue;
            if (!base) {
                // Anchor had no order yet — adopt this member's order as the anchor's.
                base = { ...mOrder };
                anchorOrderId = null;  // re-saved fresh under the anchor key
            } else {
                // Order line items live under `products` (not `items`); concatenate
                // both tables' products so the combined bill shows everything.
                base.products = [...(base.products || []), ...(mOrder.products || [])];
                base.subtotal = num(base.subtotal) + num(mOrder.subtotal);
                base.serviceCharge = num(base.serviceCharge) + num(mOrder.serviceCharge);
                base.tax = num(base.tax) + num(mOrder.tax);
                base.discount = num(base.discount) + num(mOrder.discount);
                base.total = num(base.total) + num(mOrder.total);
                base.total_items_count = (base.total_items_count || 0) + (mOrder.total_items_count || 0);
            }
            toDelete.push([mKey, mOrder._orderId || mOrder.order_id || null]);
        }

        if (toDelete.length && base) {
            base.diningOption = selectedDiningOption;
            base.tableNumber = String(anchorTable);
            // Save the combined order to the anchor FIRST; only delete member
            // drafts once it is safely persisted, so a failure can't lose items.
            await tenantStorage.saveOrder(anchorKey, base, false, !!anchorOrderId, anchorOrderId, salesDate);
            for (const [mKey, oid] of toDelete) {
                await tenantStorage.deleteOrder(mKey, oid);
            }
        }
    };

    const handleConfirmMerge = async () => {
        if (mergeSelection.length < 2) {
            setError(t('select_two_tables_to_merge') || 'Select at least two tables to merge');
            setTimeout(() => setError(''), 4000);
            return;
        }
        const anchor = String(mergeSelection[0]);
        const members = mergeSelection.slice(1).map(String);
        try {
            await occupationService.merge({
                dining_option_id: selectedDO.id,
                anchor_table_number: anchor,
                table_numbers: mergeSelection.map(String),
            });
            await combineMemberOrdersIntoAnchor(anchor, members);
            cancelMergeMode();
            await refreshAll();
            setSuccess(t('tables_merged') || 'Tables merged');
            setTimeout(() => setSuccess(''), 3000);
        } catch (e) {
            setError(e.response?.data?.detail || t('failed_to_merge') || 'Failed to merge tables');
            setTimeout(() => setError(''), 4000);
        }
    };

    const handleSplitGroup = async (occ) => {
        try {
            await occupationService.split(occ.id);
            setTablePopover(null);
            await refreshAll();
            setSuccess(t('group_split') || 'Group split');
            setTimeout(() => setSuccess(''), 3000);
        } catch (e) {
            setError(e.response?.data?.detail || t('failed_to_split') || 'Failed to split group');
            setTimeout(() => setError(''), 4000);
        }
    };

    // Split-group button, shown only when the occupation is a group anchor.
    const renderSplitGroupButton = (occ) => (
        groupByAnchor[String(occ.table_number)] ? (
            <button onClick={() => handleSplitGroup(occ)} style={{
                padding: '8px 12px', borderRadius: 8,
                border: `1.5px solid ${C.green}`, background: C.greenLight,
                color: C.greenDark, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', fontFamily: FONT,
            }}>
                {t('split_group') || 'Split Group'}
            </button>
        ) : null
    );

    const handleAddToWaitlist = async () => {
        if (!wlGuestName.trim()) return;
        setWlSubmitting(true);
        try {
            // Create guest first
            const guest = await guestService.create({ name: wlGuestName.trim(), phone: wlPhone.trim() || null });
            // Add to waitlist
            await waitingListService.add({
                guest_id: guest.id,
                party_size: wlPartySize,
                dining_option_id: selectedDO?.id || null,
            });
            setShowAddWaitlistModal(false);
            setWlGuestName('');
            setWlPhone('');
            setWlPartySize(2);
            await loadWaitlist();
            setSuccess(t('added_to_waitlist') || 'Added to waitlist');
            setTimeout(() => setSuccess(''), 3000);
        } catch (e) {
            console.error('Add to waitlist failed:', e);
            setError(e.response?.data?.detail || t('failed_to_add_waitlist') || 'Failed to add to waitlist');
            setTimeout(() => setError(''), 4000);
        } finally {
            setWlSubmitting(false);
        }
    };

    const handleAddReservation = async () => {
        if (!resGuestName.trim() || !resDate || !resTime) return;
        setResSubmitting(true);
        try {
            const guest = await guestService.create({ name: resGuestName.trim(), phone: resPhone.trim() || null });
            const scheduledFor = new Date(`${resDate}T${resTime}`);
            await reservationService.create({
                guest_id: guest.id,
                party_size: resPartySize,
                dining_option_id: selectedDO?.id || diningOptionObjects[Object.keys(diningOptionObjects)[0]]?.id,
                scheduled_for: scheduledFor.toISOString(),
                notes: resNotes.trim() || null,
            });
            setShowAddReservationModal(false);
            setResGuestName('');
            setResPhone('');
            setResPartySize(2);
            setResDate('');
            setResTime('');
            setResNotes('');
            await loadReservations();
            setSuccess(t('reservation_created') || 'Reservation created');
            setTimeout(() => setSuccess(''), 3000);
        } catch (e) {
            console.error('Create reservation failed:', e);
            setError(e.response?.data?.detail || t('failed_to_create_reservation') || 'Failed to create reservation');
            setTimeout(() => setError(''), 4000);
        } finally {
            setResSubmitting(false);
        }
    };

    const startSeatFromWaitlist = (entry) => {
        setSeatMode({
            type: 'waitlist',
            id: entry.id,
            partySize: entry.party_size,
            guestName: entry.guest?.name || entry.guest_name || '',
        });
    };

    const startSeatReservation = (res) => {
        setSeatMode({
            type: 'reservation',
            id: res.id,
            partySize: res.party_size,
            guestName: res.guest?.name || res.guest_name || '',
        });
    };

    /* ── Render ─────────────────────────────────────── */
    if (loading) {
        return (
            <div style={{
                height: '100dvh', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontFamily: FONT, background: C.bg,
            }}>
                <div style={{ textAlign: 'center', color: C.muted }}>
                    <div style={{
                        width: 32, height: 32, border: `3px solid ${C.border}`,
                        borderTopColor: C.green, borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                        margin: '0 auto 12px',
                    }} />
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    {t('loading') || 'Loading...'}
                </div>
            </div>
        );
    }

    return (
        <div style={{
            height: '100dvh', display: 'flex', flexDirection: 'column',
            overflow: 'hidden', fontFamily: FONT, background: C.bg,
        }}>
            {/* ── Alerts ─────────────────────────────── */}
            {error && (
                <div style={{
                    position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)',
                    zIndex: 9999, maxWidth: 420, width: '90%',
                }}>
                    <div style={{
                        background: C.redLight, border: `1px solid #fecaca`, borderRadius: 10,
                        padding: '10px 16px', fontSize: 13, fontWeight: 600, color: C.red,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                        <span>{error}</span>
                        <button onClick={() => setError('')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                            <CloseIcon size={14} color={C.red} />
                        </button>
                    </div>
                </div>
            )}
            {success && (
                <div style={{
                    position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)',
                    zIndex: 9999, maxWidth: 420, width: '90%',
                }}>
                    <div style={{
                        background: C.greenLight, border: `1px solid ${C.greenBorder}`, borderRadius: 10,
                        padding: '10px 16px', fontSize: 13, fontWeight: 600, color: C.greenDark,
                    }}>
                        {success}
                    </div>
                </div>
            )}

            {/* ── Seat Mode Banner ───────────────────── */}
            {seatMode && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
                    background: C.green, color: C.white,
                    padding: '10px 16px', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', fontSize: 13, fontWeight: 700,
                }}>
                    <span>
                        {seatMode.type === 'walkin'
                            ? (t('tap_table_to_seat_walkin') || 'Tap a table to seat walk-in')
                            : (t('tap_table_to_seat_guest') || `Tap a table to seat ${seatMode.guestName || 'guest'}`)}
                        {seatMode.partySize ? ` (${seatMode.partySize})` : ''}
                    </span>
                    <button onClick={() => setSeatMode(null)}
                        style={{
                            background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6,
                            padding: '4px 12px', color: C.white, fontWeight: 700,
                            cursor: 'pointer', fontSize: 12,
                        }}>
                        {t('cancel') || 'Cancel'}
                    </button>
                </div>
            )}

            {/* ── Header ─────────────────────────────── */}
            <div style={{
                height: 48, flexShrink: 0,
                background: C.white, borderBottom: `1px solid ${C.border}`,
                display: 'flex', alignItems: 'center', padding: '0 12px',
                gap: 8, marginTop: seatMode ? 40 : 0,
            }}>
                <button onClick={() => navigate('/functions')}
                    style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        padding: 6, borderRadius: 6, display: 'flex',
                    }}>
                    <BackArrow />
                </button>
                <span style={{ fontSize: 16, fontWeight: 800, color: C.text }}>
                    {t('table_service') || 'Table Service'}
                </span>
                <div style={{ flex: 1 }} />
                <button onClick={() => { setShowPinModal(true); setPinValue(''); }}
                    style={{
                        padding: '6px 14px', borderRadius: 8, border: `1px solid ${C.border}`,
                        fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        background: C.white, color: C.text,
                        whiteSpace: 'nowrap',
                    }}>
                    {t('cashier_sales') || 'Sales'}
                </button>
            </div>

            {/* ── Dining option tabs ────────────────── */}
            <div style={{
                display: 'flex', gap: 6, padding: '6px 12px',
                background: C.bg, borderBottom: `1px solid ${C.border}`,
                flexShrink: 0, overflow: 'auto', justifyContent: 'center',
            }}>
                {diningOptions.map(d => {
                    const isActive = selectedDiningOption === d.name;
                    return (
                        <button key={d.id} onClick={() => {
                            setSelectedDiningOption(d.name);
                            setTablePopover(null);
                        }}
                            style={{
                                padding: '6px 14px', borderRadius: 8, border: 'none',
                                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                background: isActive ? C.green : '#f1f5f9',
                                color: isActive ? C.white : C.muted,
                                whiteSpace: 'nowrap', transition: 'all 0.15s',
                            }}>
                            {getEntityTranslation(d, language, translationDetails) || d.name}
                        </button>
                    );
                })}
            </div>

            {/* ── Main Split ─────────────────────────── */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

                {/* Left: Floor Plan (60%) */}
                <div ref={floorRef} style={{
                    flex: '1 1 60%', minWidth: 0, position: 'relative',
                    background: C.white, borderRight: `1px solid ${C.border}`,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                    height: '100%',
                    cursor: seatMode ? 'crosshair' : 'default',
                }}
                    onClickCapture={(e) => {
                        const rect = floorRef.current?.getBoundingClientRect();
                        lastClickPos.current = {
                            x: e.clientX - (rect?.left || 0),
                            y: e.clientY - (rect?.top || 0),
                        };
                    }}
                >
                    {/* Compact legend bar */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '6px 12px', borderBottom: `1px solid ${C.border}`,
                        background: C.bg, flexShrink: 0, flexWrap: 'wrap',
                    }}>
                        {[
                            { color: '#10B981', label: t('available') || 'Available' },
                            { color: '#3B82F6', label: t('seated') || 'Seated' },
                            { color: '#EF4444', label: t('ordered') || 'Ordered' },
                            { color: '#d97706', label: t('awaiting_bill') || 'Awaiting Bill' },
                            { color: '#94a3b8', label: t('paid') || 'Paid' },
                        ].map(s => (
                            <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <div style={{
                                    width: 10, height: 10, borderRadius: '50%',
                                    background: s.color, flexShrink: 0,
                                }} />
                                <span style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>{s.label}</span>
                            </div>
                        ))}
                        <div style={{ flex: 1 }} />
                        {!seatMode && (
                            <button
                                onClick={() => (mergeMode ? cancelMergeMode() : startMergeMode())}
                                style={{
                                    padding: '5px 12px', borderRadius: 8,
                                    border: `1.5px solid ${C.green}`,
                                    background: mergeMode ? C.green : C.white,
                                    color: mergeMode ? C.white : C.green,
                                    fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                    fontFamily: FONT, whiteSpace: 'nowrap', flexShrink: 0,
                                    minHeight: 28,
                                }}>
                                {mergeMode ? (t('cancel_merge') || 'Cancel Merge') : (t('merge_tables') || 'Merge Tables')}
                            </button>
                        )}
                    </div>

                    {/* Floor plan canvas */}
                    <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                        {selectedDiningOption ? (
                            <VisualTableSelector
                                selectedDiningOption={selectedDiningOption}
                                selectedTable={null}
                                onTableSelect={(tableNumStr) => {
                                    if (seatMode) {
                                        handleSeatOnTable(tableNumStr);
                                    } else {
                                        handleTableTap(tableNumStr);
                                    }
                                }}
                                pendingOrders={pendingOrders}
                                diningOptions={diningOptionsMap}
                                occupations={filteredOccupations}
                                currency={currency}
                                isVisible={true}
                                hideHeader={true}
                                mergeMode={mergeMode}
                                mergeSelection={mergeSelection}
                            />
                        ) : (
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                height: '100%', color: C.muted, fontSize: 14,
                            }}>
                                {t('no_dining_options_with_tables') || 'No dining options with table layouts configured'}
                            </div>
                        )}
                    </div>

                    {/* Merge action bar */}
                    {mergeMode && (
                        <div style={{
                            position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 30,
                            background: C.white, borderTop: `1px solid ${C.border}`,
                            boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
                            padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12,
                        }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>
                                    {mergeSelection.length} {t('tables_selected') || 'tables selected'}
                                </div>
                                <div style={{ fontSize: 11, color: C.muted }}>
                                    {mergeSelection.length >= 1
                                        ? `${t('anchor') || 'Anchor'}: ${t('table') || 'Table'} ${mergeSelection[0]}`
                                        : (t('tap_tables_to_merge') || 'Tap tables to merge')}
                                </div>
                            </div>
                            <button onClick={cancelMergeMode} style={{
                                padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.border}`,
                                background: C.white, color: C.text, fontSize: 13, fontWeight: 700,
                                cursor: 'pointer', fontFamily: FONT, minHeight: 40,
                            }}>
                                {t('cancel') || 'Cancel'}
                            </button>
                            <button onClick={handleConfirmMerge}
                                disabled={mergeSelection.length < 2}
                                style={{
                                    padding: '8px 18px', borderRadius: 8, border: 'none',
                                    background: mergeSelection.length < 2 ? C.mutedLight : C.green,
                                    color: C.white, fontSize: 13, fontWeight: 700,
                                    cursor: mergeSelection.length < 2 ? 'not-allowed' : 'pointer',
                                    fontFamily: FONT, minHeight: 40,
                                }}>
                                {t('confirm_merge') || 'Merge'}
                            </button>
                        </div>
                    )}

                    {/* Popover backdrop — closes popover on click outside */}
                    {tablePopover && (
                        <div onClick={() => setTablePopover(null)} style={{
                            position: 'absolute', inset: 0, zIndex: 15,
                        }} />
                    )}

                    {/* Table Popover */}
                    {tablePopover && (
                        <div onClick={e => e.stopPropagation()} style={{
                            position: 'absolute',
                            left: Math.max(10, Math.min(tablePopover.x, 360)),
                            top: Math.max(10, tablePopover.y),
                            zIndex: 20,
                            background: C.white,
                            border: `1px solid ${C.border}`,
                            borderRadius: 12,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                            padding: 16,
                            minWidth: 200,
                            maxWidth: 260,
                        }}>
                            <div style={{
                                display: 'flex', alignItems: 'center',
                                justifyContent: 'space-between', marginBottom: 10,
                            }}>
                                <span style={{ fontSize: 14, fontWeight: 800, color: C.text }}>
                                    {t('table') || 'Table'} {tablePopover.tableNumber}
                                </span>
                                <button onClick={() => setTablePopover(null)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                                    <CloseIcon size={14} />
                                </button>
                            </div>

                            {tablePopover.status === 'available' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>
                                            {t('party_size') || 'Party Size'}
                                        </span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
                                            <button onClick={() => setWalkinPartySize(Math.max(1, walkinPartySize - 1))}
                                                style={{
                                                    width: 28, height: 28, borderRadius: 6, border: `1px solid ${C.border}`,
                                                    background: C.white, fontSize: 16, fontWeight: 700, cursor: 'pointer',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>-</button>
                                            <span style={{ width: 28, textAlign: 'center', fontSize: 14, fontWeight: 700 }}>
                                                {walkinPartySize}
                                            </span>
                                            <button onClick={() => setWalkinPartySize(walkinPartySize + 1)}
                                                style={{
                                                    width: 28, height: 28, borderRadius: 6, border: `1px solid ${C.border}`,
                                                    background: C.white, fontSize: 16, fontWeight: 700, cursor: 'pointer',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>+</button>
                                        </div>
                                    </div>
                                    <button onClick={handleSeatWalkIn} style={{
                                        padding: '8px 12px', borderRadius: 8, border: 'none',
                                        background: C.green, color: C.white, fontSize: 13,
                                        fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                                    }}>
                                        {t('seat_walk_in') || 'Seat Walk-in'}
                                    </button>
                                </div>
                            )}

                            {tablePopover.status === 'seated' && tablePopover.occupation && (
                                <div>
                                    {tablePopover.occupation.guest_name && (
                                        <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>
                                            {tablePopover.occupation.guest_name}
                                        </div>
                                    )}
                                    <div style={{
                                        fontSize: 11, color: C.mutedLight, marginBottom: 10,
                                        display: 'flex', gap: 8,
                                    }}>
                                        <span>{t('party') || 'Party'}: {tablePopover.occupation.party_size}</span>
                                        <span>{timeAgo(tablePopover.occupation.seated_at)}</span>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <button onClick={() => handleNewOrder(tablePopover.occupation)} style={{
                                            padding: '8px 12px', borderRadius: 8, border: 'none',
                                            background: C.green, color: C.white, fontSize: 13,
                                            fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                                        }}>
                                            {t('new_order') || 'New Order'}
                                        </button>
                                        <button onClick={() => startChangeTable(tablePopover.occupation)} style={{
                                            padding: '8px 12px', borderRadius: 8,
                                            border: `1.5px solid #3B82F6`, background: '#eff6ff',
                                            color: '#3B82F6', fontSize: 13, fontWeight: 600,
                                            cursor: 'pointer', fontFamily: FONT,
                                        }}>
                                            {t('change_table') || 'Change Table'}
                                        </button>
                                        {renderSplitGroupButton(tablePopover.occupation)}
                                        <button onClick={() => handleClearTable(tablePopover.occupation)} style={{
                                            padding: '8px 12px', borderRadius: 8,
                                            border: `1.5px solid ${C.red}`, background: C.redLight,
                                            color: C.red, fontSize: 13, fontWeight: 600,
                                            cursor: 'pointer', fontFamily: FONT,
                                        }}>
                                            {t('clear_table') || 'Clear Table'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {tablePopover.status === 'ordered' && tablePopover.occupation && (
                                <div>
                                    {tablePopover.occupation.guest_name && (
                                        <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>
                                            {tablePopover.occupation.guest_name}
                                        </div>
                                    )}
                                    <div style={{
                                        fontSize: 11, color: C.mutedLight, marginBottom: 6,
                                        display: 'flex', gap: 8,
                                    }}>
                                        <span>{t('party') || 'Party'}: {tablePopover.occupation.party_size}</span>
                                        <span>{timeAgo(tablePopover.occupation.seated_at)}</span>
                                    </div>
                                    {tablePopover.occupation.served_at && (
                                        <div style={{
                                            fontSize: 11, fontWeight: 700, color: '#16a34a',
                                            background: '#eff5ff', border: '1px solid #bbf7d0',
                                            borderRadius: 6, padding: '4px 8px', marginBottom: 8,
                                            textAlign: 'center',
                                        }}>
                                            {t('served') || 'Served'}
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <button onClick={() => handleAddItems(tablePopover.occupation)} style={{
                                            padding: '8px 12px', borderRadius: 8, border: 'none',
                                            background: C.green, color: C.white, fontSize: 13,
                                            fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                                        }}>
                                            {t('add_items') || 'Add Items'}
                                        </button>
                                        {!tablePopover.occupation.served_at && (
                                            <button onClick={() => handleMarkServed(tablePopover.occupation)} style={{
                                                padding: '8px 12px', borderRadius: 8, border: 'none',
                                                background: C.green, color: C.white, fontSize: 13,
                                                fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                                            }}>
                                                {t('mark_served') || 'Mark Served'}
                                            </button>
                                        )}
                                        <button onClick={() => handleRequestBill(tablePopover.occupation)} style={{
                                            padding: '8px 12px', borderRadius: 8, border: 'none',
                                            background: '#d97706', color: C.white, fontSize: 13,
                                            fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                                        }}>
                                            {t('request_bill') || 'Request Bill'}
                                        </button>
                                        <button onClick={() => startChangeTable(tablePopover.occupation)} style={{
                                            padding: '8px 12px', borderRadius: 8,
                                            border: `1.5px solid #3B82F6`, background: '#eff6ff',
                                            color: '#3B82F6', fontSize: 13, fontWeight: 600,
                                            cursor: 'pointer', fontFamily: FONT,
                                        }}>
                                            {t('change_table') || 'Change Table'}
                                        </button>
                                        {renderSplitGroupButton(tablePopover.occupation)}
                                        <button onClick={() => handleClearTable(tablePopover.occupation)} style={{
                                            padding: '8px 12px', borderRadius: 8,
                                            border: `1.5px solid ${C.red}`, background: C.redLight,
                                            color: C.red, fontSize: 13, fontWeight: 600,
                                            cursor: 'pointer', fontFamily: FONT,
                                        }}>
                                            {t('clear_table') || 'Clear Table'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {tablePopover.status === 'awaiting_bill' && tablePopover.occupation && (
                                <div>
                                    <div style={{ fontSize: 12, color: C.amber, fontWeight: 700, marginBottom: 8 }}>
                                        {t('awaiting_bill') || 'Awaiting Bill'}
                                    </div>
                                    <button onClick={() => handleClearTable(tablePopover.occupation)} style={{
                                        width: '100%', padding: '8px 12px', borderRadius: 8, border: 'none',
                                        background: C.muted, color: C.white, fontSize: 13,
                                        fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                                    }}>
                                        {t('clear_table') || 'Clear Table'}
                                    </button>
                                </div>
                            )}

                            {tablePopover.status === 'paid' && tablePopover.occupation && (
                                <div>
                                    <div style={{ fontSize: 12, color: C.mutedLight, fontWeight: 700, marginBottom: 8 }}>
                                        {t('paid') || 'Paid'}
                                    </div>
                                    <button onClick={() => handleClearTable(tablePopover.occupation)} style={{
                                        width: '100%', padding: '8px 12px', borderRadius: 8, border: 'none',
                                        background: C.green, color: C.white, fontSize: 13,
                                        fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                                    }}>
                                        {t('clear_table') || 'Clear Table'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Right: Waitlist / Reservations (40%) */}
                <div style={{
                    flex: '0 0 40%', maxWidth: '40%', display: 'flex', flexDirection: 'column',
                    overflow: 'hidden', background: C.bg,
                }}>
                    {/* Tabs */}
                    <div style={{
                        display: 'flex', borderBottom: `1px solid ${C.border}`,
                        background: C.white, flexShrink: 0,
                    }}>
                        {['waitlist', 'reservations'].map(tab => {
                            const active = activeTab === tab;
                            const count = tab === 'waitlist' ? waitlistEntries.length : upcomingReservations.length;
                            return (
                                <button key={tab} onClick={() => setActiveTab(tab)}
                                    style={{
                                        flex: 1, padding: '10px 0', border: 'none',
                                        borderBottom: active ? `2px solid ${C.green}` : '2px solid transparent',
                                        background: 'transparent', cursor: 'pointer',
                                        fontSize: 12, fontWeight: 700, fontFamily: FONT,
                                        color: active ? C.green : C.muted,
                                        display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', gap: 6,
                                    }}>
                                    {tab === 'waitlist' ? (t('waitlist') || 'Waitlist') : (t('reservations') || 'Reservations')}
                                    {count > 0 && (
                                        <span style={{
                                            background: active ? C.greenLight : '#f1f5f9',
                                            color: active ? C.green : C.muted,
                                            fontSize: 10, fontWeight: 800,
                                            padding: '1px 6px', borderRadius: 10,
                                        }}>
                                            {count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Tab content */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>

                        {/* ── Waitlist Tab ─────────────── */}
                        {activeTab === 'waitlist' && (
                            <div>
                                {/* Add button */}
                                <button onClick={() => setShowAddWaitlistModal(true)}
                                    style={{
                                        width: '100%', padding: '8px 0', borderRadius: 8,
                                        border: `1.5px dashed ${C.greenBorder}`,
                                        background: C.greenLight, color: C.green,
                                        fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                        fontFamily: FONT, display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', gap: 6,
                                        marginBottom: 10,
                                    }}>
                                    <PlusIcon size={14} color={C.green} />
                                    {t('add_to_waitlist') || 'Add to Waitlist'}
                                </button>

                                {waitlistEntries.length === 0 ? (
                                    <div style={{
                                        textAlign: 'center', padding: '32px 16px',
                                        color: C.mutedLight, fontSize: 13,
                                    }}>
                                        <UsersIcon size={28} color={C.border} />
                                        <div style={{ marginTop: 8 }}>
                                            {t('no_guests_waiting') || 'No guests waiting'}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {waitlistEntries.map((entry, idx) => (
                                            <div key={entry.id} style={{
                                                background: C.white, borderRadius: 10,
                                                border: `1px solid ${C.border}`,
                                                padding: '10px 12px',
                                                display: 'flex', alignItems: 'center', gap: 10,
                                            }}>
                                                {/* Position */}
                                                <div style={{
                                                    width: 28, height: 28, borderRadius: '50%',
                                                    background: C.greenLight, color: C.green,
                                                    fontSize: 12, fontWeight: 800,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}>
                                                    {idx + 1}
                                                </div>
                                                {/* Info */}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{
                                                        fontSize: 13, fontWeight: 700, color: C.text,
                                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                    }}>
                                                        {entry.guest?.name || entry.guest_name || t('guest') || 'Guest'}
                                                    </div>
                                                    {entry.guest?.phone && (
                                                        <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
                                                            {entry.guest.phone}
                                                        </div>
                                                    )}
                                                    <div style={{
                                                        display: 'flex', alignItems: 'center', gap: 8,
                                                        fontSize: 11, color: C.muted, marginTop: 2,
                                                    }}>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                                            <UsersIcon size={10} color={C.muted} /> {entry.party_size}
                                                        </span>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                                            <ClockSmallIcon size={10} color={C.muted} /> {timeAgo(entry.created_at || entry.added_at)}
                                                        </span>
                                                        {statusBadge(entry.status, t)}
                                                    </div>
                                                </div>
                                                {/* Seat button */}
                                                {['waiting', 'notified'].includes(entry.status) && (
                                                    <button onClick={() => startSeatFromWaitlist(entry)}
                                                        style={{
                                                            padding: '5px 12px', borderRadius: 6, border: 'none',
                                                            background: C.green, color: C.white,
                                                            fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                                            fontFamily: FONT, flexShrink: 0,
                                                        }}>
                                                        {t('seat') || 'Seat'}
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Reservations Tab ────────── */}
                        {activeTab === 'reservations' && (
                            <div>
                                <button
                                    onClick={() => {
                                        const today = new Date();
                                        setResDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
                                        setResTime('');
                                        setShowAddReservationModal(true);
                                    }}
                                    style={{
                                        width: '100%', padding: '10px 0', borderRadius: 8,
                                        border: `1.5px dashed ${C.greenBorder}`,
                                        background: C.greenLight, color: C.green,
                                        fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                        fontFamily: FONT, marginBottom: 12,
                                    }}
                                >
                                    + {t('add_reservation') || 'Add Reservation'}
                                </button>
                                {upcomingReservations.length === 0 && pastReservations.length === 0 ? (
                                    <div style={{
                                        textAlign: 'center', padding: '32px 16px',
                                        color: C.mutedLight, fontSize: 13,
                                    }}>
                                        <ClockSmallIcon size={28} color={C.border} />
                                        <div style={{ marginTop: 8 }}>
                                            {t('no_reservations_today') || 'No reservations today'}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {/* Upcoming */}
                                        {upcomingReservations.length > 0 && (
                                            <div style={{ marginBottom: 16 }}>
                                                <div style={{
                                                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                                                    letterSpacing: 1, color: C.muted, marginBottom: 8,
                                                }}>
                                                    {t('upcoming') || 'Upcoming'}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                    {upcomingReservations.map(res => (
                                                        <div key={res.id} style={{
                                                            background: C.white, borderRadius: 10,
                                                            border: `1px solid ${C.border}`,
                                                            padding: '10px 12px',
                                                            display: 'flex', alignItems: 'center', gap: 10,
                                                        }}>
                                                            {/* Time */}
                                                            <div style={{
                                                                fontSize: 13, fontWeight: 800, color: C.green,
                                                                minWidth: 48, flexShrink: 0,
                                                            }}>
                                                                {formatTime(res.scheduled_for)}
                                                            </div>
                                                            {/* Info */}
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{
                                                                    fontSize: 13, fontWeight: 700, color: C.text,
                                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                                }}>
                                                                    {res.guest?.name || res.guest_name || t('guest') || 'Guest'}
                                                                </div>
                                                                <div style={{
                                                                    display: 'flex', alignItems: 'center', gap: 8,
                                                                    fontSize: 11, color: C.muted, marginTop: 2,
                                                                }}>
                                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                                                        <UsersIcon size={10} color={C.muted} /> {res.party_size}
                                                                    </span>
                                                                    {res.table_number && (
                                                                        <span>{t('table') || 'Table'} {res.table_number}</span>
                                                                    )}
                                                                    {statusBadge(res.status, t)}
                                                                </div>
                                                            </div>
                                                            {/* Seat button */}
                                                            {['booked', 'confirmed'].includes(res.status) && (
                                                                <button onClick={() => startSeatReservation(res)}
                                                                    style={{
                                                                        padding: '5px 12px', borderRadius: 6, border: 'none',
                                                                        background: C.green, color: C.white,
                                                                        fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                                                        fontFamily: FONT, flexShrink: 0,
                                                                    }}>
                                                                    {t('seat') || 'Seat'}
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Past */}
                                        {pastReservations.length > 0 && (
                                            <div>
                                                <div style={{
                                                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                                                    letterSpacing: 1, color: C.mutedLight, marginBottom: 8,
                                                }}>
                                                    {t('past') || 'Past'}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, opacity: 0.6 }}>
                                                    {pastReservations.map(res => (
                                                        <div key={res.id} style={{
                                                            background: C.white, borderRadius: 10,
                                                            border: `1px solid ${C.border}`,
                                                            padding: '8px 12px',
                                                            display: 'flex', alignItems: 'center', gap: 10,
                                                        }}>
                                                            <div style={{
                                                                fontSize: 12, fontWeight: 700, color: C.mutedLight,
                                                                minWidth: 48, flexShrink: 0,
                                                            }}>
                                                                {formatTime(res.scheduled_for)}
                                                            </div>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{
                                                                    fontSize: 12, fontWeight: 600, color: C.muted,
                                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                                }}>
                                                                    {res.guest?.name || res.guest_name || t('guest') || 'Guest'}
                                                                </div>
                                                                <div style={{
                                                                    display: 'flex', alignItems: 'center', gap: 6,
                                                                    fontSize: 10, color: C.mutedLight, marginTop: 2,
                                                                }}>
                                                                    <UsersIcon size={9} color={C.mutedLight} /> {res.party_size}
                                                                    {statusBadge(res.status, t)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Add to Waitlist Modal ──────────────── */}
            {showAddWaitlistModal && (
                <div
                    style={{
                        position: 'fixed', inset: 0, zIndex: 2000,
                        background: 'rgba(0,0,0,0.45)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 20, fontFamily: FONT,
                    }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowAddWaitlistModal(false); }}
                    onKeyDown={(e) => { if (e.key === 'Escape') setShowAddWaitlistModal(false); }}
                >
                    <div style={{
                        background: C.white, borderRadius: 16, width: '100%', maxWidth: 380,
                        boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
                    }}>
                        {/* Header */}
                        <div style={{
                            padding: '16px 20px', borderBottom: `1px solid ${C.border}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <span style={{ fontSize: 16, fontWeight: 800, color: C.text }}>
                                {t('add_to_waitlist') || 'Add to Waitlist'}
                            </span>
                            <button onClick={() => setShowAddWaitlistModal(false)}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    padding: 4, display: 'flex',
                                }}>
                                <CloseIcon size={16} />
                            </button>
                        </div>
                        {/* Body */}
                        <div style={{ padding: '20px' }}>
                            {/* Guest name */}
                            <div style={{ marginBottom: 16 }}>
                                <label style={{
                                    display: 'block', fontSize: 11, fontWeight: 700,
                                    textTransform: 'uppercase', letterSpacing: 0.8,
                                    color: C.muted, marginBottom: 6,
                                }}>
                                    {t('guest_name') || 'Guest Name'}
                                </label>
                                <input
                                    type="text"
                                    value={wlGuestName}
                                    onChange={(e) => setWlGuestName(e.target.value)}
                                    autoFocus
                                    placeholder={t('enter_guest_name') || 'Enter guest name'}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && wlGuestName.trim()) handleAddToWaitlist(); }}
                                    style={{
                                        width: '100%', boxSizing: 'border-box',
                                        padding: '10px 14px', borderRadius: 8,
                                        border: `1.5px solid ${C.border}`, fontSize: 14,
                                        fontFamily: FONT, color: C.text, outline: 'none',
                                    }}
                                />
                            </div>
                            {/* Contact number */}
                            <div style={{ marginBottom: 16 }}>
                                <label style={{
                                    display: 'block', fontSize: 11, fontWeight: 700,
                                    textTransform: 'uppercase', letterSpacing: 0.8,
                                    color: C.muted, marginBottom: 6,
                                }}>
                                    {t('contact_number') || 'Contact Number'}
                                </label>
                                <input
                                    type="tel"
                                    value={wlPhone}
                                    onChange={(e) => setWlPhone(e.target.value)}
                                    placeholder={t('enter_contact_number') || 'Enter contact number'}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && wlGuestName.trim()) handleAddToWaitlist(); }}
                                    style={{
                                        width: '100%', boxSizing: 'border-box',
                                        padding: '10px 14px', borderRadius: 8,
                                        border: `1.5px solid ${C.border}`, fontSize: 14,
                                        fontFamily: FONT, color: C.text, outline: 'none',
                                    }}
                                />
                            </div>
                            {/* Party size */}
                            <div style={{ marginBottom: 20 }}>
                                <label style={{
                                    display: 'block', fontSize: 11, fontWeight: 700,
                                    textTransform: 'uppercase', letterSpacing: 0.8,
                                    color: C.muted, marginBottom: 6,
                                }}>
                                    {t('party_size') || 'Party Size'}
                                </label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <button onClick={() => setWlPartySize(Math.max(1, wlPartySize - 1))}
                                        style={{
                                            width: 36, height: 36, borderRadius: 8,
                                            border: `1.5px solid ${C.border}`, background: C.white,
                                            fontSize: 18, cursor: 'pointer', color: C.text,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                        -
                                    </button>
                                    <span style={{
                                        fontSize: 20, fontWeight: 800, color: C.text,
                                        minWidth: 32, textAlign: 'center',
                                    }}>
                                        {wlPartySize}
                                    </span>
                                    <button onClick={() => setWlPartySize(wlPartySize + 1)}
                                        style={{
                                            width: 36, height: 36, borderRadius: 8,
                                            border: `1.5px solid ${C.border}`, background: C.white,
                                            fontSize: 18, cursor: 'pointer', color: C.text,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                        +
                                    </button>
                                </div>
                            </div>
                            {/* Actions */}
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button onClick={() => setShowAddWaitlistModal(false)}
                                    style={{
                                        flex: 1, padding: '10px 0', borderRadius: 8,
                                        border: `1.5px solid ${C.border}`, background: C.white,
                                        color: C.muted, fontSize: 14, fontWeight: 600,
                                        cursor: 'pointer', fontFamily: FONT,
                                    }}>
                                    {t('cancel') || 'Cancel'}
                                </button>
                                <button
                                    onClick={handleAddToWaitlist}
                                    disabled={!wlGuestName.trim() || wlSubmitting}
                                    style={{
                                        flex: 2, padding: '10px 0', borderRadius: 8, border: 'none',
                                        background: wlGuestName.trim() ? C.green : C.border,
                                        color: wlGuestName.trim() ? C.white : C.muted,
                                        fontSize: 14, fontWeight: 700,
                                        cursor: wlGuestName.trim() ? 'pointer' : 'not-allowed',
                                        fontFamily: FONT,
                                    }}>
                                    {wlSubmitting ? '...' : (t('add') || 'Add')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Add Reservation Modal ──────────────── */}
            {showAddReservationModal && (
                <div
                    style={{
                        position: 'fixed', inset: 0, zIndex: 2000,
                        background: 'rgba(0,0,0,0.45)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 20, fontFamily: FONT,
                    }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowAddReservationModal(false); }}
                    onKeyDown={(e) => { if (e.key === 'Escape') setShowAddReservationModal(false); }}
                >
                    <div style={{
                        background: C.white, borderRadius: 14, padding: 24,
                        width: '100%', maxWidth: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    }}>
                        <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 20 }}>
                            {t('add_reservation') || 'Add Reservation'}
                        </div>
                        {/* Guest name */}
                        <div style={{ marginBottom: 16 }}>
                            <label style={{
                                display: 'block', fontSize: 11, fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: 0.8,
                                color: C.muted, marginBottom: 6,
                            }}>
                                {t('guest_name') || 'Guest Name'} <span style={{ color: C.red }}>*</span>
                            </label>
                            <input
                                type="text"
                                value={resGuestName}
                                onChange={(e) => setResGuestName(e.target.value)}
                                autoFocus
                                placeholder={t('enter_guest_name') || 'Enter guest name'}
                                style={{
                                    width: '100%', padding: '10px 12px', borderRadius: 8,
                                    border: `1.5px solid ${C.border}`, fontSize: 14,
                                    fontFamily: FONT, outline: 'none', boxSizing: 'border-box',
                                }}
                            />
                        </div>
                        {/* Contact number */}
                        <div style={{ marginBottom: 16 }}>
                            <label style={{
                                display: 'block', fontSize: 11, fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: 0.8,
                                color: C.muted, marginBottom: 6,
                            }}>
                                {t('contact_number') || 'Contact Number'}
                            </label>
                            <input
                                type="tel"
                                value={resPhone}
                                onChange={(e) => setResPhone(e.target.value)}
                                placeholder={t('enter_contact_number') || 'Enter contact number'}
                                style={{
                                    width: '100%', padding: '10px 12px', borderRadius: 8,
                                    border: `1.5px solid ${C.border}`, fontSize: 14,
                                    fontFamily: FONT, outline: 'none', boxSizing: 'border-box',
                                }}
                            />
                        </div>
                        {/* Date & Time */}
                        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                            <div style={{ flex: 1 }}>
                                <label style={{
                                    display: 'block', fontSize: 11, fontWeight: 700,
                                    textTransform: 'uppercase', letterSpacing: 0.8,
                                    color: C.muted, marginBottom: 6,
                                }}>
                                    {t('date') || 'Date'} <span style={{ color: C.red }}>*</span>
                                </label>
                                <input
                                    type="date"
                                    value={resDate}
                                    onChange={(e) => setResDate(e.target.value)}
                                    style={{
                                        width: '100%', padding: '10px 12px', borderRadius: 8,
                                        border: `1.5px solid ${C.border}`, fontSize: 14,
                                        fontFamily: FONT, outline: 'none', boxSizing: 'border-box',
                                    }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{
                                    display: 'block', fontSize: 11, fontWeight: 700,
                                    textTransform: 'uppercase', letterSpacing: 0.8,
                                    color: C.muted, marginBottom: 6,
                                }}>
                                    {t('time') || 'Time'} <span style={{ color: C.red }}>*</span>
                                </label>
                                <input
                                    type="time"
                                    value={resTime}
                                    onChange={(e) => setResTime(e.target.value)}
                                    style={{
                                        width: '100%', padding: '10px 12px', borderRadius: 8,
                                        border: `1.5px solid ${C.border}`, fontSize: 14,
                                        fontFamily: FONT, outline: 'none', boxSizing: 'border-box',
                                    }}
                                />
                            </div>
                        </div>
                        {/* Party size */}
                        <div style={{ marginBottom: 16 }}>
                            <label style={{
                                display: 'block', fontSize: 11, fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: 0.8,
                                color: C.muted, marginBottom: 6,
                            }}>
                                {t('party_size') || 'Party Size'}
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <button onClick={() => setResPartySize(Math.max(1, resPartySize - 1))}
                                    style={{
                                        width: 36, height: 36, borderRadius: 8,
                                        border: `1.5px solid ${C.border}`, background: C.white,
                                        fontSize: 18, cursor: 'pointer', color: C.text,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                    -
                                </button>
                                <span style={{
                                    fontSize: 20, fontWeight: 800, color: C.text,
                                    minWidth: 32, textAlign: 'center',
                                }}>
                                    {resPartySize}
                                </span>
                                <button onClick={() => setResPartySize(resPartySize + 1)}
                                    style={{
                                        width: 36, height: 36, borderRadius: 8,
                                        border: `1.5px solid ${C.border}`, background: C.white,
                                        fontSize: 18, cursor: 'pointer', color: C.text,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                    +
                                </button>
                            </div>
                        </div>
                        {/* Notes */}
                        <div style={{ marginBottom: 20 }}>
                            <label style={{
                                display: 'block', fontSize: 11, fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: 0.8,
                                color: C.muted, marginBottom: 6,
                            }}>
                                {t('notes') || 'Notes'}
                            </label>
                            <input
                                type="text"
                                value={resNotes}
                                onChange={(e) => setResNotes(e.target.value)}
                                placeholder={t('special_requests') || 'Special requests...'}
                                style={{
                                    width: '100%', padding: '10px 12px', borderRadius: 8,
                                    border: `1.5px solid ${C.border}`, fontSize: 14,
                                    fontFamily: FONT, outline: 'none', boxSizing: 'border-box',
                                }}
                            />
                        </div>
                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => setShowAddReservationModal(false)}
                                style={{
                                    flex: 1, padding: '10px 0', borderRadius: 8,
                                    border: `1.5px solid ${C.border}`, background: C.white,
                                    color: C.muted, fontSize: 14, fontWeight: 600,
                                    cursor: 'pointer', fontFamily: FONT,
                                }}>
                                {t('cancel') || 'Cancel'}
                            </button>
                            <button
                                onClick={handleAddReservation}
                                disabled={!resGuestName.trim() || !resDate || !resTime || resSubmitting}
                                style={{
                                    flex: 2, padding: '10px 0', borderRadius: 8, border: 'none',
                                    background: (resGuestName.trim() && resDate && resTime) ? C.green : C.border,
                                    color: (resGuestName.trim() && resDate && resTime) ? C.white : C.muted,
                                    fontSize: 14, fontWeight: 700,
                                    cursor: (resGuestName.trim() && resDate && resTime) ? 'pointer' : 'not-allowed',
                                    fontFamily: FONT,
                                }}>
                                {resSubmitting ? '...' : (t('add_reservation') || 'Add Reservation')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* PIN Modal for Cashier Sales */}
            <Modal isOpen={showPinModal} onClose={() => setShowPinModal(false)} title={t('cashier_sales') || 'Cashier Sales'}>
                <form onSubmit={handlePinSubmit} className="space-y-4">
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                            {t('Enter your 6-digit PIN')} <span style={{ color: C.red }}>*</span>
                        </label>
                        <input
                            type="password" maxLength={6} pattern="[0-9]{6}" inputMode="numeric"
                            placeholder="••••••" value={pinValue} autoFocus required
                            onChange={e => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                        <button type="button" onClick={() => setShowPinModal(false)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50">
                            {t('Cancel')}
                        </button>
                        <button type="submit" disabled={pinLoading}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 disabled:bg-gray-400">
                            {pinLoading ? t('Authenticating...') : t('Login')}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* ── Waiter Order Drawer ─────────────────── */}
            <WaiterOrderDrawer
                isOpen={!!orderDrawer}
                onClose={() => setOrderDrawer(null)}
                mode={orderDrawer?.mode || 'new'}
                diningOptionName={selectedDiningOption}
                diningOptionObject={selectedDO}
                tableNumber={orderDrawer?.tableNumber}
                occupation={orderDrawer?.occupation || null}
                existingOrder={orderDrawer?.existingOrder || null}
                onOrderPlaced={async () => { await refreshAll(); }}
            />
        </div>
    );
};

export default TableServicePage;
